import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePayment } from '../../contexts/PaymentContext';
import { useAppointment } from '../../contexts/AppointmentContext';
import { ArrowLeft, Check, AlertCircle, Calendar, Clock, Video, Mic, MessageCircle, CreditCard, Building2, Wallet, CheckCircle2, XCircle } from 'lucide-react';

const mockServices = [
  {
    id: '1',
    name: 'One-time Teleconsultation',
    description: 'Connect with a qualified doctor for a single consultation session',
    price: 1500,
    duration: '30 minutes',
    icon: '📞',
  },
  {
    id: '2',
    name: 'Antenatal Care Review',
    description: 'Comprehensive pregnancy check-up and consultation',
    price: 2500,
    duration: '45 minutes',
    icon: '🤰',
  },
  {
    id: '3',
    name: 'Postnatal Follow-up',
    description: 'Post-delivery health check for mother and baby',
    price: 2000,
    duration: '40 minutes',
    icon: '👶',
  },
  {
    id: '4',
    name: 'Child Health Check-up',
    description: 'Complete health assessment for your child',
    price: 1800,
    duration: '35 minutes',
    icon: '🏥',
  },
  {
    id: '5',
    name: 'Immunization Consultation',
    description: 'Guidance on vaccination schedule and administration',
    price: 1200,
    duration: '25 minutes',
    icon: '💉',
  },
  {
    id: '6',
    name: 'Specialist Consultation',
    description: 'Connect with a specialist (Obstetrician/Pediatrician)',
    price: 3500,
    duration: '45 minutes',
    icon: '👨‍⚕️',
  },
];

const mockDoctors = [
  { id: '1', name: 'Dr. Amina Yusuf', specialty: 'Obstetrics', available: true },
  { id: '2', name: 'Dr. Chidi Okafor', specialty: 'Pediatrics', available: true },
  { id: '3', name: 'Dr. Folake Adeyemi', specialty: 'Obstetrics', available: false },
];

const ServiceCheckout = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { payForService } = usePayment();
  const { createAppointment } = useAppointment();
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [consultationType, setConsultationType] = useState('video');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const service = mockServices.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 md:p-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="p-4 bg-red-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Service Not Found</h2>
          <p className="text-gray-600 mb-6">The requested service could not be located. Please select a service from our available options.</p>
          <button
            onClick={() => navigate('/patient/health')}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-md"
          >
            Browse Services
          </button>
        </div>
      </div>
    );
  }

  const handlePayment = () => {
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      const success = Math.random() > 0.1; // 90% success rate
      
      if (success) {
        payForService(service);
        
        // Create appointment after successful payment
        const doctor = mockDoctors.find(d => d.id === selectedDoctor);
        const appointmentDate = preferredDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const appointmentTime = preferredTime || '10:00 AM';
        
        createAppointment({
          date: appointmentDate,
          time: appointmentTime,
          doctor: doctor?.name || 'Dr. Amina Yusuf',
          specialty: doctor?.specialty || 'General',
          type: service.name,
          mode: consultationType,
          serviceId: service.id,
          amount: service.price,
        });
        
        setPaymentStatus('success');
      } else {
        setPaymentStatus('failed');
      }
      setIsProcessing(false);
    }, 2000);
  };

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 md:p-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-2xl w-full">
          <div className="text-center mb-6">
            <div className="p-4 bg-emerald-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-emerald-600 mb-2">Payment Successful!</h2>
            <p className="text-gray-600">
              Your payment of <span className="font-bold text-gray-900">₦{service.price.toLocaleString()}</span> has been processed successfully.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 mb-6 border border-emerald-200">
            <h3 className="font-bold text-emerald-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Appointment Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-emerald-700 font-medium">Service:</span>
                <span className="text-sm font-bold text-gray-900 text-right">{service.name}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-emerald-700 font-medium">Amount Paid:</span>
                <span className="text-sm font-bold text-emerald-600">₦{service.price.toLocaleString()}</span>
              </div>
              {selectedDoctor && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-emerald-700 font-medium">Doctor:</span>
                  <span className="text-sm font-bold text-gray-900">{mockDoctors.find(d => d.id === selectedDoctor)?.name}</span>
                </div>
              )}
              {preferredDate && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-emerald-700 font-medium">Date:</span>
                  <span className="text-sm font-bold text-gray-900">{new Date(preferredDate).toLocaleDateString()}</span>
                </div>
              )}
              {preferredTime && (
                <div className="flex justify-between items-start">
                  <span className="text-sm text-emerald-700 font-medium">Time:</span>
                  <span className="text-sm font-bold text-gray-900">{preferredTime}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-sm text-emerald-700 font-medium">Consultation Type:</span>
                <span className="text-sm font-bold text-gray-900 capitalize">{consultationType}</span>
              </div>
              <div className="flex justify-between items-start pt-3 border-t border-emerald-200">
                <span className="text-sm text-emerald-700 font-medium">Status:</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                  <Clock className="w-3 h-3" />
                  Pending Confirmation
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 text-sm mb-1">Next Steps</h4>
                <p className="text-sm text-blue-800">
                  Your appointment is pending doctor confirmation. You'll receive a notification once confirmed. 
                  Please check your Appointments page to track the status and join the consultation when it's time.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/patient/appointments')}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-md flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              View Appointments
            </button>
            <button
              onClick={() => navigate('/patient/dashboard')}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 md:p-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="p-4 bg-red-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-red-600 mb-3">Payment Failed</h2>
          <p className="text-gray-600 mb-6">
            Unfortunately, we couldn't process your payment. Please check your payment details and try again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                setPaymentStatus(null);
                setIsProcessing(false);
              }}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-md"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/patient/health')}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all"
            >
              Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/patient/health')}
            className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium mb-4 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Services
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Checkout</h1>
              <p className="text-gray-600 text-sm">Complete your booking and secure your appointment</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Service Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-600" />
                Selected Service
              </h2>
              <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                <span className="text-4xl">{service.icon}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{service.name}</h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{service.description}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-gray-700 border border-emerald-200 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {service.duration}
                    </span>
                    <span className="text-xl font-bold text-emerald-600">
                      ₦{service.price.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Doctor Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Choose Your Doctor</h2>
              <div className="space-y-2.5">
                {mockDoctors.map(doctor => (
                  <label
                    key={doctor.id}
                    className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedDoctor === doctor.id
                        ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                        : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                    } ${!doctor.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedDoctor === doctor.id
                          ? 'border-emerald-600 bg-emerald-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedDoctor === doctor.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{doctor.name}</h4>
                        <p className="text-sm text-gray-600">{doctor.specialty} Specialist</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                      doctor.available
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {doctor.available ? '✓ Available' : 'Unavailable'}
                    </span>
                    <input
                      type="radio"
                      name="doctor"
                      value={doctor.id}
                      checked={selectedDoctor === doctor.id}
                      onChange={(e) => setSelectedDoctor(e.target.value)}
                      disabled={!doctor.available}
                      className="sr-only"
                    />
                  </label>
                ))}
              </div>
            </div>

            {/* Appointment Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Schedule Your Appointment
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Preferred Date */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={preferredDate}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>

                {/* Preferred Time */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Select Time
                  </label>
                  <select
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white transition-all"
                  >
                    <option value="">Choose a time slot</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>
              </div>

              {/* Consultation Type */}
              <div className="mt-5">
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  How would you like to connect?
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <label className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    consultationType === 'video' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="consultationType"
                      value="video"
                      checked={consultationType === 'video'}
                      onChange={(e) => setConsultationType(e.target.value)}
                      className="sr-only"
                    />
                    <Video className={`w-7 h-7 mb-2 ${consultationType === 'video' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-semibold ${consultationType === 'video' ? 'text-emerald-700' : 'text-gray-600'}`}>
                      Video Call
                    </span>
                  </label>
                  <label className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    consultationType === 'audio' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="consultationType"
                      value="audio"
                      checked={consultationType === 'audio'}
                      onChange={(e) => setConsultationType(e.target.value)}
                      className="sr-only"
                    />
                    <Mic className={`w-7 h-7 mb-2 ${consultationType === 'audio' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-semibold ${consultationType === 'audio' ? 'text-emerald-700' : 'text-gray-600'}`}>
                      Voice Call
                    </span>
                  </label>
                  <label className={`flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    consultationType === 'chat' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="consultationType"
                      value="chat"
                      checked={consultationType === 'chat'}
                      onChange={(e) => setConsultationType(e.target.value)}
                      className="sr-only"
                    />
                    <MessageCircle className={`w-7 h-7 mb-2 ${consultationType === 'chat' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-semibold ${consultationType === 'chat' ? 'text-emerald-700' : 'text-gray-600'}`}>
                      Chat
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Method</h2>
              <div className="space-y-2.5">
                <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentMethod === 'card' 
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm' 
                    : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                }`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'card'
                      ? 'border-emerald-600 bg-emerald-600'
                      : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'card' && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${paymentMethod === 'card' ? 'text-gray-900' : 'text-gray-600'}`}>
                    Debit/Credit Card
                  </span>
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                </label>
                
                <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentMethod === 'bank' 
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm' 
                    : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                }`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'bank'
                      ? 'border-emerald-600 bg-emerald-600'
                      : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'bank' && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Building2 className={`w-5 h-5 ${paymentMethod === 'bank' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${paymentMethod === 'bank' ? 'text-gray-900' : 'text-gray-600'}`}>
                    Bank Transfer
                  </span>
                  <input
                    type="radio"
                    name="payment"
                    value="bank"
                    checked={paymentMethod === 'bank'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                </label>
                
                <label className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  paymentMethod === 'wallet' 
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm' 
                    : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                }`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'wallet'
                      ? 'border-emerald-600 bg-emerald-600'
                      : 'border-gray-300'
                  }`}>
                    {paymentMethod === 'wallet' && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Wallet className={`w-5 h-5 ${paymentMethod === 'wallet' ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${paymentMethod === 'wallet' ? 'text-gray-900' : 'text-gray-600'}`}>
                    Digital Wallet
                  </span>
                  <input
                    type="radio"
                    name="payment"
                    value="wallet"
                    checked={paymentMethod === 'wallet'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="sr-only"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                <div className="flex justify-between text-gray-600">
                  <span className="text-sm">Service Fee</span>
                  <span className="font-semibold">₦{service.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span className="text-sm">Processing Fee</span>
                  <span className="font-semibold text-emerald-600">FREE</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center mb-6 pb-6 border-b border-gray-200">
                <span className="font-bold text-gray-900 text-lg">Total Amount</span>
                <span className="font-bold text-3xl text-emerald-600">₦{service.price.toLocaleString()}</span>
              </div>

              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className={`w-full py-4 rounded-xl font-bold transition-all text-white shadow-md ${
                  isProcessing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 hover:shadow-lg transform hover:-translate-y-0.5'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing Payment...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Proceed to Payment
                  </span>
                )}
              </button>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 text-center leading-relaxed">
                  🔒 Secure payment powered by industry-standard encryption. Your payment details are safe with us.
                </p>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
                By completing this payment, you agree to our <span className="text-emerald-600 font-medium">Terms of Service</span> and <span className="text-emerald-600 font-medium">Privacy Policy</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCheckout;